import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import {
  Card,
  Button,
  Table,
  Form,
  InputGroup,
  Spinner,
  Badge,
  Modal,
  Alert,
  Pagination
} from 'react-bootstrap';
import { FaPlus, FaSearch, FaEdit, FaTrash, FaExclamationTriangle } from 'react-icons/fa';

// Add debounce function for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const Medicines = () => {
  const { user } = useContext(AuthContext);
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredMedicines, setFilteredMedicines] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [medicineToDelete, setMedicineToDelete] = useState(null);
  const [stockThreshold, setStockThreshold] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  const debouncedSearchQuery = useDebounce(searchQuery, 500); // 500ms delay

  const fetchMedicines = async (page = 1) => {
    setLoading(true);
    try {
      const res = await axios.get(`/api/medicines.php?page=${page}&limit=20`);
      console.log('Medicines API response:', res.data);
      
      // Handle different response formats
      if (res.data.medicines) {
        setMedicines(res.data.medicines);
        setFilteredMedicines(res.data.medicines);
        setPagination(res.data.pagination);
      } else if (Array.isArray(res.data)) {
        // If response is directly an array
        setMedicines(res.data);
        setFilteredMedicines(res.data);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: res.data.length,
          itemsPerPage: 20
        });
      } else {
        setMedicines([]);
        setFilteredMedicines([]);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching medicines:', err);
      setMedicines([]);
      setFilteredMedicines([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMedicines();
  }, []);

  const performSearch = async (query, page = 1) => {
    if (query.trim() === '') {
      fetchMedicines(page);
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.get(`/api/medicines.php?search=${query}&page=${page}&limit=20`);
      console.log('Search API response:', res.data);
      
      // Handle different response formats
      if (res.data.medicines) {
        setFilteredMedicines(res.data.medicines);
        setPagination(res.data.pagination);
      } else if (Array.isArray(res.data)) {
        setFilteredMedicines(res.data);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalItems: res.data.length,
          itemsPerPage: 20
        });
      } else {
        setFilteredMedicines([]);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error searching medicines:', err);
      setFilteredMedicines([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    performSearch(debouncedSearchQuery, 1);
  }, [debouncedSearchQuery]);

  const handlePageChange = (page) => {
    if (debouncedSearchQuery.trim() === '') {
      fetchMedicines(page);
    } else {
      performSearch(debouncedSearchQuery, page);
    }
  };

  const updateStock = async (id, newStock) => {
    try {
      await axios.put(`/api/medicines.php?id=${id}`, {
        stock: newStock
      });

      setMedicines(
        medicines.map(medicine =>
          medicine.id === id ? { ...medicine, stock: newStock } : medicine
        )
      );

      setFilteredMedicines(
        filteredMedicines.map(medicine =>
          medicine.id === id ? { ...medicine, stock: newStock } : medicine
        )
      );
    } catch (err) {
      console.error(err);
    }
  };

  // Handle delete medicine
  const handleDeleteClick = (medicine) => {
    setMedicineToDelete(medicine);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!medicineToDelete) return;
    
    try {
      await axios.delete(`/api/medicines.php?id=${medicineToDelete.id}`);
      
      // Remove medicine from state
      setMedicines(medicines.filter(m => m.id !== medicineToDelete.id));
      setFilteredMedicines(filteredMedicines.filter(m => m.id !== medicineToDelete.id));
      setShowDeleteModal(false);
      setMedicineToDelete(null);
      
      // Refresh current page if needed
      if (filteredMedicines.length === 1 && pagination.currentPage > 1) {
        handlePageChange(pagination.currentPage - 1);
      } else if (filteredMedicines.length === 1) {
        handlePageChange(1);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const isLowStock = (medicine) => {
    return medicine.stock <= stockThreshold;
  };
  
  // Create pagination controls
  const renderPaginationItems = () => {
    let items = [];
    
    // Add "Previous" button
    items.push(
      <Pagination.Prev 
        key="prev" 
        onClick={() => handlePageChange(pagination.currentPage - 1)}
        disabled={pagination.currentPage === 1}
      />
    );
    
    // Always show first page
    items.push(
      <Pagination.Item
        key={1}
        active={pagination.currentPage === 1}
        onClick={() => handlePageChange(1)}
      >
        1
      </Pagination.Item>
    );
    
    // Add ellipsis if needed
    if (pagination.currentPage > 3) {
      items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }
    
    // Add pages around current page
    let startPage = Math.max(2, pagination.currentPage - 1);
    let endPage = Math.min(pagination.totalPages - 1, pagination.currentPage + 1);
    
    for (let i = startPage; i <= endPage; i++) {
      if (i > 1 && i < pagination.totalPages) {
        items.push(
          <Pagination.Item 
            key={i} 
            active={i === pagination.currentPage}
            onClick={() => handlePageChange(i)}
          >
            {i}
          </Pagination.Item>
        );
      }
    }
    
    // Add ellipsis if needed
    if (pagination.currentPage < pagination.totalPages - 2) {
      items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
    }
    
    // Always show last page if there is more than one page
    if (pagination.totalPages > 1) {
      items.push(
        <Pagination.Item 
          key={pagination.totalPages} 
          active={pagination.currentPage === pagination.totalPages}
          onClick={() => handlePageChange(pagination.totalPages)}
        >
          {pagination.totalPages}
        </Pagination.Item>
      );
    }
    
    // Add "Next" button
    items.push(
      <Pagination.Next 
        key="next" 
        onClick={() => handlePageChange(pagination.currentPage + 1)}
        disabled={pagination.currentPage === pagination.totalPages}
      />
    );
    
    return items;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Medicines</h2>
        {(user?.role === 'admin' || user?.permissions?.addMedicine) && (
          <Link to="/medicines/add">
            <Button variant="primary">
              <FaPlus className="me-2" /> Add New Medicine
            </Button>
          </Link>
        )}
      </div>

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <InputGroup>
            <Form.Control
              placeholder="Search medicine by name"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            <Button variant="outline-secondary" disabled>
              <FaSearch />
            </Button>
          </InputGroup>
          <div className="ms-3 d-flex align-items-center">
            <Form.Label className="mb-0 me-2">Low Stock Alert:</Form.Label>
            <Form.Control
              type="number"
              value={stockThreshold}
              onChange={e => setStockThreshold(Number(e.target.value))}
              style={{ width: '70px' }}
              min="0"
            />
          </div>
        </Card.Header>
        <Card.Body>
          {loading ? (
            <div className="text-center my-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : filteredMedicines.length > 0 ? (
            <>
              {filteredMedicines.some(med => isLowStock(med)) && (
                <Alert variant="warning" className="d-flex align-items-center mb-3">
                  <FaExclamationTriangle className="me-2" />
                  <span>Some medicines are running low on stock (below threshold of {stockThreshold})!</span>
                </Alert>
              )}
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Unit</th>
                    <th>Stock</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMedicines.map(medicine => (
                    <tr key={medicine.id} className={isLowStock(medicine) ? 'table-warning' : ''}>
                      <td>{medicine.name}</td>
                      <td>
                        <Badge bg={
                          medicine.type === 'Tablet' ? 'primary' :
                          medicine.type === 'Capsule' ? 'success' :
                          medicine.type === 'Syrup' ? 'info' :
                          medicine.type === 'Injection' ? 'warning' :
                          medicine.type === 'Drip' ? 'danger' :
                          'secondary'
                        }>
                          {medicine.type}
                        </Badge>
                      </td>
                      <td>{medicine.unit}</td>
                      <td>
                        {(user?.role === 'admin' || user?.permissions?.editMedicine) ? (
                          <InputGroup size="sm" style={{ width: '150px' }}>
                            <Button
                              variant="outline-secondary"
                              onClick={() => updateStock(medicine.id, Math.max(0, medicine.stock - 1))}
                            >
                              -
                            </Button>
                            <Form.Control
                              value={medicine.stock}
                              onChange={e => updateStock(medicine.id, Number(e.target.value))}
                              min="0"
                              type="number"
                              className={`text-center ${isLowStock(medicine) ? 'bg-warning bg-opacity-10' : ''}`}
                            />
                            <Button
                              variant="outline-secondary"
                              onClick={() => updateStock(medicine.id, medicine.stock + 1)}
                            >
                              +
                            </Button>
                          </InputGroup>
                        ) : (
                          <span className="fw-bold">{medicine.stock}</span>
                        )}
                        {isLowStock(medicine) && (
                          <small className="text-danger">
                            <FaExclamationTriangle className="me-1" /> Low stock!
                          </small>
                        )}
                      </td>
                      <td>
                        {(user?.role === 'admin' || user?.permissions?.editMedicine) && (
                          <Link to={`/medicines/edit/${medicine.id}`} className="btn btn-sm btn-warning me-2">
                            <FaEdit className="me-1" /> Edit
                          </Link>
                        )}
                        {(user?.role === 'admin' || user?.permissions?.deleteMedicine) && (
                          <Button 
                            variant="danger" 
                            size="sm"
                            onClick={() => handleDeleteClick(medicine)}
                          >
                            <FaTrash className="me-1" /> Delete
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div>
                    Showing {filteredMedicines.length} of {pagination.totalItems} medicines
                  </div>
                  <Pagination>{renderPaginationItems()}</Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="text-center my-5">
              <p>No medicines found. Please add a new medicine.</p>
              <Link to="/medicines/add">
                <Button variant="primary">
                  <FaPlus className="me-2" /> Add New Medicine
                </Button>
              </Link>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete <strong>{medicineToDelete?.name}</strong>?
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete}>
            Delete Medicine
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Medicines; 
